import { useCallback, useEffect, useState } from "react";

import { useFileUpload } from "@/api/hooks/useFileUpload";
import { ApiResponse } from "@/api/rest/core/types";
import { HouseholdData } from "@/api/rest/models/household";
import { uploadFiles } from "@/api/rest/services/uploadService";
import { uploadWarrantyDraftFiles, WarrantyDraftFile } from "@/api/rest/services/warrantyService";
import { debug } from "@/utils/debug/logger";

interface UseSubmissionFormProps {
  householdData: HouseholdData;
  draftId?: number | null;
}

export const useSubmissionForm = ({ householdData, draftId }: UseSubmissionFormProps) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadedDraftFiles, setUploadedDraftFiles] = useState<WarrantyDraftFile[]>([]);
  const [isSubmittingCase, setSubmittingState] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState<boolean | null>(null);
  // One id per submission: retries reuse it so the backend returns the same case; a reset starts a new one.
  const [clientSubmissionId, setClientSubmissionId] = useState(() => crypto.randomUUID());
  // Files picked before a draft exists upload on submit; their progress and any submit error show by the button.
  const { upload, progress: uploadProgress, error: submitError, setError: setSubmitError } = useFileUpload();

  const isFileUploadMandatory = householdData?.featureFlags?.FILE_UPLOAD_MANDATORY === true;

  debug.log("useSubmissionForm", "isFileUploadMandatory", isFileUploadMandatory);

  useEffect(() => {
    if (pendingFiles.length > 0 || uploadedDraftFiles.length > 0) {
      debug.log("Files pending", pendingFiles, uploadedDraftFiles);
      setIsFileUploaded(true);
    } else {
      setIsFileUploaded(null);
    }
  }, [pendingFiles, uploadedDraftFiles]);

  debug.log("isFileUploaded", isFileUploaded);

  const submitFiles = useCallback(
    async (files: File[], onProgress: (fraction: number) => void): Promise<ApiResponse<unknown>> => {
      console.log(
        "[FileUpload] useSubmissionForm submitFiles: draftId=%s, filesCount=%d, fileDetails=%o",
        draftId,
        files.length,
        files.map((f) => ({ name: f.name, path: (f as File & { path?: string }).path, size: f.size, type: f.type })),
      );

      if (draftId) {
        const uploads = await uploadFiles(files, onProgress);
        if (!uploads.success) {
          return uploads;
        }
        const result = await uploadWarrantyDraftFiles(draftId, uploads.result);
        if (result.success) {
          setUploadedDraftFiles(result.result.files);
        }
        return result;
      }

      setPendingFiles((prevFiles) => [...prevFiles, ...files]);
      return Promise.resolve({ success: true, result: null });
    },
    [draftId],
  );

  const uploadPendingFiles = useCallback(() => upload(pendingFiles), [upload, pendingFiles]);

  const resetSubmission = useCallback(() => {
    setPendingFiles([]);
    setUploadedDraftFiles([]);
    setIsFileUploaded(null);
    setSubmittingState(false);
    setSubmitError(null);
    setClientSubmissionId(crypto.randomUUID());
  }, [setSubmitError]);

  return {
    pendingFiles,
    setPendingFiles,
    uploadedDraftFiles,
    setUploadedDraftFiles,
    isSubmittingCase,
    setSubmittingState,
    isFileUploaded,
    setIsFileUploaded,
    isFileUploadMandatory,
    submitFiles,
    uploadPendingFiles,
    uploadProgress,
    submitError,
    setSubmitError,
    resetSubmission,
    clientSubmissionId,
  };
};
